import { useRef } from "react";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import { Label } from "@shared-kernel/primary/shared/shadcn/ui/label";
import { Loader2, Upload, X } from "lucide-react";
import type { RequestState } from "@redux/state/shared-types";
import { isImageFile } from "@shared-kernel/domain/types/file-validation";
import { PdfPreviewHoverCard } from "@shared-kernel/primary/shared/pdf-preview-hover-card/pdf-preview-hover-card";

interface UploadButtonProps {
  // Core functionality
  onFileSelect: (file: File) => void;
  onFileDelete?: () => void;
  onError?: (message: string) => void;
  "data-testid"?: string;

  // Visual variant
  variant?: "default" | "compact";

  // File configuration
  accept?: string[];
  maxSize?: number; // in bytes
  value?: string | null;

  // Validation
  validate?: (file: File) => string | null;

  // UI customization
  label?: string;
  buttonText?: string;
  helpText?: string;
  showPreview?: boolean;
  processing?: RequestState;
  error?: string;

  // Optional customization
  className?: string;
  previewClassName?: string;
  disabled?: boolean;
}

export const UploadButton = ({
  onFileSelect,
  onFileDelete,
  onError,
  variant = "default",
  accept = ["application/pdf"],
  maxSize,
  value,
  validate,
  label,
  buttonText = "Choisir un fichier",
  helpText,
  showPreview = false,
  processing = "idle",
  error,
  className,
  previewClassName,
  disabled = false,
  "data-testid": dataTestId,
}: UploadButtonProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Size validation
    if (maxSize && file.size > maxSize) {
      onError?.(`La taille du fichier dépasse la limite de ${Math.round(maxSize / 1024 / 1024)} Mo`);
      resetFileInput();
      return;
    }

    // Custom validation
    if (validate) {
      const validationError = validate(file);
      if (validationError) {
        onError?.(validationError);
        resetFileInput();
        return;
      }
    }

    onFileSelect(file);
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClick = () => {
    // Reset the input value to allow reselecting the same file in various scenarios
    if ((processing === "failed" || error) && fileInputRef.current) {
      resetFileInput();
    }
    fileInputRef.current?.click();
  };

  const isImage = value !== undefined && isImageFile(value);

  return (
    <div className={cn("space-y-2", className)}>
      {label && <Label htmlFor={label}>{label}</Label>}

      <div
        className={cn("rounded-lg transition-colors", {
          "border-2 border-dashed p-4": variant === "default",
          "border-input": !error && !["pending", "failed"].includes(processing) && variant === "default",
          "border-blue-400 bg-blue-50 dark:bg-blue-950/50": processing === "pending" && variant === "default",
          "border-red-400 bg-red-50 dark:bg-red-950/50": (error || processing === "failed") && variant === "default",
        })}
      >
        <input
          type="file"
          ref={fileInputRef}
          accept={accept.join(",")}
          onChange={handleFileSelect}
          className="hidden"
          id={label}
          data-testid={dataTestId}
        />

        {/* File Preview */}
        {value && (
          <div className="mb-3 flex items-center justify-center gap-2">
            {showPreview && isImage ? (
              <div className={cn("h-32 w-32 overflow-hidden rounded", previewClassName)}>
                <img src={value} alt="Preview" className="size-full object-cover" />
              </div>
            ) : (
              <PdfPreviewHoverCard
                documentUrl={value}
                triggerLabel="Voir le fichier"
                iframeTitle="Aperçu du fichier"
                iconSize="md"
                triggerClassName="max-w-[200px] truncate"
              />
            )}
          </div>
        )}

        {/* Upload Button */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleClick}
            disabled={["pending"].includes(processing) || disabled}
            type="button"
          >
            {processing === "pending" ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Téléchargement...
              </>
            ) : (
              <>
                <Upload className="mr-2 size-4" />
                {value ? "Changer le fichier" : buttonText}
              </>
            )}
          </Button>

          {value && onFileDelete && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                resetFileInput();
                onFileDelete?.();
              }}
              disabled={["pending"].includes(processing) || disabled}
              className="text-red-500 hover:text-red-600"
              type="button"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>

        {/* Error & Help Text */}
        {(error || processing === "failed") && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">
            {error || "Une erreur est survenue lors du téléchargement du fichier. Veuillez réessayer."}
          </p>
        )}
        {helpText && !error && <p className="mt-2 text-xs text-muted-foreground">{helpText}</p>}
      </div>
    </div>
  );
};
